import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ApiResponse,
  BookingResponse,
  QRCodeResponse,
  CreditCardResponse,
  BookingRequest,
  QRCodeRequest,
  CreditCardRequest,
} from './type'
import crypto from 'crypto'
import { signature } from '~/utils/const'

const API_BASE_URL = process.env.NEXT_PUBLIC_BEV_URL
const EPAY_URL = process.env.NEXT_PUBLIC_EPAY_URL

const fetcher = async (url: string) => {
  const response = await fetch(url, {
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching data: ${response.status}`)
  }

  return await response.json()
}

const postData = async (url: string, data: any, options = {}) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    ...options,
  })

  if (!response.ok) {
    console.error('error', response)
  }

  return await response.json()
}
const postDataQrCode = async (url: string, data: any, customer_code: string, options = {}) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      accept: '*/*',
      'Content-Type': 'application/json',
      'customer-code': customer_code,
    },
    body: data,
    ...options,
  })

  if (!response.ok) {
    console.error('error', response)
  }

  return await response.json()
}
export function useBooking(bookingNo?: string) {
  const {
    data,
    error,
    isLoading: queryLoading,
  } = useQuery<ApiResponse<BookingResponse>>({
    queryKey: ['booking', bookingNo],
    queryFn: () => fetcher(`${API_BASE_URL}/be/booking/${bookingNo}`),
    enabled: !!bookingNo,
  })

  const createBookingMutation = useMutation<ApiResponse<BookingResponse>, Error, BookingRequest>({
    mutationFn: (requestData: BookingRequest) =>
      postData(`${API_BASE_URL}/be/booking/create`, requestData),
  })

  return {
    booking: data?.data,
    isLoading: queryLoading || createBookingMutation.isPending,
    isError: error || createBookingMutation.error,
    createBooking: createBookingMutation.mutate,
  }
}

export function useQRCode(bookingNo?: string) {
  const queryClient = useQueryClient()

  const cachedQRCode = queryClient.getQueryData<ApiResponse<QRCodeResponse>>(['qrCode', bookingNo])

  const mutation = useMutation<ApiResponse<QRCodeResponse>, Error, QRCodeRequest>({
    mutationFn: (requestData: QRCodeRequest) =>
      postData(`${API_BASE_URL}/booking/booking-payment/gen-qr`, requestData),
    onSuccess: (data) => {
      // Cache the QR code data
      queryClient.setQueryData(['qrCode', bookingNo], data)
    },
  })

  return {
    generateQRCode: mutation.mutate,
    isLoading: mutation.isPending,
    isError: mutation.error,
    qrCode: cachedQRCode?.data || mutation.data?.data,
  }
}

export function useCreditCardPayment(customer_code: string) {
  const createCreditCardPaymentMutation = useMutation<CreditCardResponse, Error, CreditCardRequest>(
    {
      mutationFn: async (requestData: CreditCardRequest) => {
        const encrypted = encryptAESBase64(requestData, signature.encrypt_key) || ''
        return postDataQrCode(`${EPAY_URL}/epay/order/create`, encrypted, customer_code)
      },
    }
  )

  return {
    createCreditCardPayment: createCreditCardPaymentMutation.mutate,
  }
}

function encryptAESBase64(data: any, key: string): string {
  // Chuyển đổi key sang Buffer
  const keyBuffer = Buffer.from(key, 'utf8')
  // Kiểm tra độ dài key (AES-192 yêu cầu 24 bytes)
  if (keyBuffer.length !== 24) {
    throw new Error('Key phải có đúng 24 bytes!')
  }

  // ⚠️ Đảm bảo dữ liệu đầu vào giống Java (không dùng JSON.stringify)
  const formattedData = JSON.stringify(data)

  // Tạo cipher AES-192-ECB (không cần IV)
  const cipher = crypto.createCipheriv('aes-192-ecb', keyBuffer, Buffer.alloc(0))
  cipher.setAutoPadding(true)

  // Mã hóa dữ liệu và chuyển sang Base64
  let encryptedData = cipher.update(formattedData, 'utf8', 'base64')
  encryptedData += cipher.final('base64')

  return encryptedData
}
